import { storage } from '~/shared/storage'

/**
 * The shell's namespace, shared with the other session-adjacent state the shell persists (see
 * `lastCustomer`).
 */
const shellStorage = storage.for('shell')

const EMR_SESSION_KEY = 'emrSession'

/**
 * The persisted **EMR session**: the token an EMR launch established, and when it expires.
 *
 * Both fields are absent between the halves of a launch, because the record is written when the
 * launch route starts redeeming its handoff code and filled in when the token arrives. A record with
 * no token therefore reads as "EMR-embedded mode, no session yet" rather than as "no launch".
 *
 * Persisted rather than held in memory so a reload inside the EHR keeps the session: the launch is
 * driven by the EHR and its handoff code is single-use, so a lost token cannot be re-fetched. That
 * puts a bearer token in `localStorage`, which is the posture the suite already has for the Auth0
 * session (`cacheLocation="localstorage"`).
 */
export interface EmrSessionRecord {
  accessToken?: string
  /** Epoch milliseconds at which `accessToken` expires. */
  expiresAt?: number
}

/**
 * Reads the stored record, or null when no launch has happened in this browser.
 *
 * A value that is not the expected shape reads as no record: it can only come from a released
 * version of the suite that wrote something else, and treating it as absent degrades to "log in
 * again" rather than to a crash.
 */
export const readEmrSessionRecord = (): EmrSessionRecord | null => {
  const stored = shellStorage.get(EMR_SESSION_KEY)
  if (stored === null) {
    return null
  }

  try {
    const parsed: unknown = JSON.parse(stored)
    return typeof parsed === 'object' && parsed !== null ? parsed : null
  } catch {
    return null
  }
}

/** Stores the record, replacing whatever a previous launch left. */
export const writeEmrSessionRecord = (record: EmrSessionRecord): void => {
  shellStorage.set(EMR_SESSION_KEY, JSON.stringify(record))
}

/** Removes the record, which is what takes the browser back out of EMR-embedded mode. */
export const clearEmrSessionRecord = (): void => {
  shellStorage.remove(EMR_SESSION_KEY)
}
